import { useCallback, useEffect, useRef, useState } from 'react'
import ga from 'src/utils/pixels/gtag'

type ABTestVariant = 'control' | 'test'

const STORAGE_KEY_PREFIX = 'ab_test_'

/**
 * Simple A/B test hook with GA4 + Vercel Analytics tracking.
 * Use for future experiments (e.g. new_cta_text, checkout_flow_v2).
 *
 * @param experimentId - Unique identifier (e.g. 'cover_photo', 'new_checkout_step')
 * @param options.shouldTrack - Fire exposure/conversion events (default: true)
 * @param options.assign - Assign (and persist) a variant when none is stored (default: true).
 *   `false` only READS an existing assignment — for follow-up surfaces that must not enrol a
 *   visitor who never saw the experiment's entry (variant stays unassigned).
 *
 * How it works:
 * - Assigns a persistent variant per user (stored in localStorage)
 * - Sets a GA4 user property so ALL subsequent events can be segmented by variant
 * - Fires `ab_test_exposure` when the component mounts (for exposure counts)
 * - Call `trackConversion()` on the success action (e.g. card click, form submit)
 *
 * Vercel Analytics: Create experiments in the dashboard with matching
 * experiment IDs. Exposure and conversion events auto-populate.
 *
 * GA4: Create a user-scoped custom dimension `ab_{experimentId}` to segment
 * reports by variant.
 *
 * @example
 * const { isTest, trackConversion } = useABTest('new_cta', { shouldTrack: true })
 * return (
 *   <button onClick={() => { trackConversion(); onSubmit() }}>
 *     {isTest ? 'Get Started' : 'Sign Up'}
 *   </button>
 * )
 */
export function useABTest(
  experimentId: string,
  {
    shouldTrack = true,
    assign = true
  }: { shouldTrack?: boolean; assign?: boolean } = {}
): {
  variant: ABTestVariant
  isTest: boolean
  /** False until the persisted/assigned variant is read after mount (variant reads 'control' until then). */
  isAssigned: boolean
  trackConversion: () => void
} {
  const [variant, setVariant] = useState<ABTestVariant | null>(null)
  const tracked = useRef(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const key = `${STORAGE_KEY_PREFIX}${experimentId}`
      const stored = localStorage.getItem(key)
      if (stored === 'control' || stored === 'test') {
        setVariant(stored)
        return
      }
      if (!assign) return
      const assigned: ABTestVariant = Math.random() < 0.5 ? 'control' : 'test'
      localStorage.setItem(key, assigned)
      setVariant(assigned)
    } catch {
      setVariant('control')
    }
  }, [experimentId, assign])

  useEffect(() => {
    if (!variant || tracked.current || !shouldTrack) return
    tracked.current = true

    const gtag = ga.getGtag()
    gtag('set', 'user_properties', {
      [`ab_${experimentId}`]: variant
    })

    ga.event('ab_test_exposure', {
      experiment: experimentId,
      variant
    })
  }, [variant, experimentId, shouldTrack])

  const trackConversion = useCallback(() => {
    if (!variant || !shouldTrack) return
    ga.event('ab_test_conversion', {
      experiment: experimentId,
      variant
    })
  }, [variant, experimentId, shouldTrack])

  return {
    variant: variant ?? 'control',
    isTest: variant === 'test',
    isAssigned: variant !== null,
    trackConversion
  }
}
